/**
 * Copy of the Mayker App (events-admin-dashboard) app/lib/displayDate.cjs — keep the two
 * identical so the portal, packets and the proposal app print dates the same way.
 *
 * Shared date / time display standard. Display only: nothing here reads or
 * writes stored values, and no input is parsed through `new Date(string)`, so
 * the browser or server time zone cannot move a calendar day.
 *
 *   Nov 4, 2026 · Nov 4–5, 2026 · Nov 30–Dec 2, 2026 · Dec 30, 2026–Jan 2, 2027
 *   5:00 PM · 5:00 PM CT · Nov 4, 2026, 5:00 PM CT
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const EN_DASH = '–';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function validParts(year, month, day) {
  if (!year || month < 1 || month > 12 || day < 1) return null;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > daysInMonth) return null;
  return { year, month, day };
}

/**
 * Calendar parts from a stored date. Accepts `YYYY-MM-DD` (optionally followed by
 * a time, as JSON-serialised Postgres dates are), `MM-DD-YYYY`, `MM/DD/YYYY`, or a
 * Date object. A Date at local midnight is read with local getters (browser-built
 * dates); any other Date with UTC getters (node-postgres `date` on a UTC server).
 */
export function calendarParts(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const localMidnight = value.getHours() === 0 && value.getMinutes() === 0
      && value.getSeconds() === 0 && value.getMilliseconds() === 0;
    return localMidnight
      ? validParts(value.getFullYear(), value.getMonth() + 1, value.getDate())
      : validParts(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
  }
  const raw = String(value).trim();
  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[T\s])/);
  if (m) return validParts(Number(m[1]), Number(m[2]), Number(m[3]));
  m = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) return validParts(Number(m[3]), Number(m[1]), Number(m[2]));
  return null;
}

/** `YYYY-MM-DD`, or '' when the value is not a calendar date. */
export function toYmd(value) {
  const p = calendarParts(value);
  return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)}` : '';
}

/** `Nov 4, 2026`, or '' when the value is not a calendar date. */
export function formatDisplayDate(value) {
  const p = calendarParts(value);
  return p ? `${MONTHS[p.month - 1]} ${p.day}, ${p.year}` : '';
}

/** `Wed, Nov 4` — operational schedules where the weekday matters. */
export function formatDisplayWeekdayDate(value, { withYear = false } = {}) {
  const p = calendarParts(value);
  if (!p) return '';
  const weekday = WEEKDAYS[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()];
  return `${weekday}, ${MONTHS[p.month - 1]} ${p.day}${withYear ? `, ${p.year}` : ''}`;
}

/**
 * Event date range. One day (or a missing end) prints as a single date; ranges
 * use an en dash and repeat only what changes. A range across a year boundary
 * names both years.
 */
export function formatDisplayDateRange(startValue, endValue) {
  const s = calendarParts(startValue);
  const e = calendarParts(endValue);
  if (!s && !e) return '';
  if (!s) return formatDisplayDate(endValue);
  if (!e) return formatDisplayDate(startValue);
  const sm = MONTHS[s.month - 1];
  const em = MONTHS[e.month - 1];
  if (s.year !== e.year) {
    return `${sm} ${s.day}, ${s.year}${EN_DASH}${em} ${e.day}, ${e.year}`;
  }
  if (s.month !== e.month) {
    return `${sm} ${s.day}${EN_DASH}${em} ${e.day}, ${s.year}`;
  }
  if (s.day !== e.day) {
    return `${sm} ${s.day}${EN_DASH}${e.day}, ${s.year}`;
  }
  return `${sm} ${s.day}, ${s.year}`;
}

/** Same as formatDisplayDateRange, from a proposal-shaped object. */
export function formatProposalDisplayDates(proposal) {
  if (!proposal) return '';
  return formatDisplayDateRange(
    proposal.startDate ?? proposal.start_date,
    proposal.endDate ?? proposal.end_date,
  );
}

const ZONE_LABELS = {
  'America/New_York': 'ET',
  'America/Detroit': 'ET',
  'America/Indiana/Indianapolis': 'ET',
  'America/Kentucky/Louisville': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Boise': 'MT',
  'America/Phoenix': 'MT',
  'America/Los_Angeles': 'PT',
  'America/Anchorage': 'AKT',
  'Pacific/Honolulu': 'HT',
};

const LEGACY_ZONE_LABELS = {
  EST: 'ET', EDT: 'ET', ET: 'ET', EASTERN: 'ET',
  CST: 'CT', CDT: 'CT', CT: 'CT', CENTRAL: 'CT',
  MST: 'MT', MDT: 'MT', MT: 'MT', MOUNTAIN: 'MT',
  PST: 'PT', PDT: 'PT', PT: 'PT', PACIFIC: 'PT',
  AKST: 'AKT', AKDT: 'AKT', AKT: 'AKT',
  HST: 'HT', HT: 'HT',
};

/**
 * Operational zone label (`CT`, `ET`, `MT`, `PT`, `AKT`, `HT`) for an IANA zone or a
 * legacy abbreviation. The label does not change with daylight saving time.
 * Unknown zones return ''.
 */
export function zoneLabel(zone) {
  const raw = String(zone || '').trim();
  if (!raw) return '';
  if (ZONE_LABELS[raw]) return ZONE_LABELS[raw];
  const legacy = LEGACY_ZONE_LABELS[raw.toUpperCase()];
  if (legacy) return legacy;
  if (!raw.includes('/')) return '';
  // Other US IANA zones (America/Indiana/Knox, …): use the January abbreviation.
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone: raw, timeZoneName: 'short' })
      .formatToParts(new Date(Date.UTC(2026, 0, 15, 18)))
      .find((item) => item.type === 'timeZoneName')?.value;
    return LEGACY_ZONE_LABELS[String(name || '').toUpperCase()] || '';
  } catch {
    return '';
  }
}

const ZONE_NAMES = { ET: 'Eastern Time', CT: 'Central Time', MT: 'Mountain Time', PT: 'Pacific Time', AKT: 'Alaska Time', HT: 'Hawaii Time' };

/** `Central Time` for an IANA zone or legacy abbreviation; '' when unknown. */
export function zoneName(zone) {
  return ZONE_NAMES[zoneLabel(zone)] || '';
}

/** `5:00 PM CT`. The time text is passed through unchanged when no zone is known. */
export function withZoneLabel(timeText, zone) {
  const time = String(timeText || '').trim();
  if (!time) return '';
  const label = zoneLabel(zone);
  return label ? `${time} ${label}` : time;
}

/**
 * An instant (timestamp) as `Nov 4, 2026, 5:00 PM CT`, read in the given zone
 * (default Central, where staff work).
 */
export function formatDisplayInstant(value, zone = 'America/Chicago') {
  const date = value instanceof Date ? value : new Date(value);
  if (value == null || value === '' || Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const part = (type) => parts.find((item) => item.type === type)?.value || '';
  const month = MONTHS[Number(part('month')) - 1];
  const period = part('dayPeriod').toUpperCase();
  const label = zoneLabel(zone);
  return `${month} ${part('day')}, ${part('year')}, ${part('hour')}:${part('minute')} ${period}${label ? ` ${label}` : ''}`;
}

/** The calendar date of an instant in the given zone, as `Nov 4, 2026`. */
export function formatDisplayInstantDate(value, zone = 'America/Chicago') {
  const date = value instanceof Date ? value : new Date(value);
  if (value == null || value === '' || Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric',
  }).formatToParts(date);
  const part = (type) => Number(parts.find((item) => item.type === type)?.value || 0);
  return formatDisplayDate(`${part('year')}-${pad2(part('month'))}-${pad2(part('day'))}`);
}
